import Api from "@/config/Api";

const state = {
  isOpen: false,
  sessions: [],
  currentSessionId: null,
  currentMessages: [],
  isLoading: false,
  isSending: false,
  usage: null, // { requests, max_requests, cost, max_cost }
  credits: null, // { used, limit, unlimited, remaining, percentage }
  pendingConfirmation: null, // { text, credits }
  isLoadingSession: false,
  suggestions: null // chips dinámicos según estado real del negocio
};

const mutations = {
  SET_OPEN(state, val) {
    state.isOpen = val;
  },
  SET_SESSIONS(state, sessions) {
    state.sessions = sessions;
  },
  SET_CURRENT_SESSION(state, id) {
    state.currentSessionId = id;
  },
  SET_CURRENT_MESSAGES(state, messages) {
    state.currentMessages = messages;
  },
  ADD_MESSAGE(state, msg) {
    state.currentMessages.push(msg);
  },
  REMOVE_MESSAGE(state, id) {
    state.currentMessages = state.currentMessages.filter(m => m.id !== id);
  },
  SET_LOADING(state, val) {
    state.isLoading = val;
  },
  SET_SENDING(state, val) {
    state.isSending = val;
  },
  UPDATE_SESSION_TITLE(state, { id, title }) {
    const s = state.sessions.find(s => s.id === id);
    if (s) s.title = title;
  },
  UPDATE_SESSION_PREVIEW(state, { id, preview, updated_at }) {
    const s = state.sessions.find(s => s.id === id);
    if (s) {
      s.preview = preview;
      s.updated_at = updated_at;
    }
  },
  REMOVE_SESSION(state, id) {
    state.sessions = state.sessions.filter(s => s.id !== id);
    if (state.currentSessionId === id) {
      state.currentSessionId = null;
      state.currentMessages = [];
    }
  },
  ADD_SESSION(state, session) {
    state.sessions.unshift(session);
  },
  SET_USAGE(state, usage) {
    state.usage = usage;
  },
  SET_CREDITS(state, credits) {
    state.credits = credits;
  },
  SET_PENDING_CONFIRMATION(state, data) {
    state.pendingConfirmation = data;
  },
  CLEAR_PENDING_CONFIRMATION(state) {
    state.pendingConfirmation = null;
  },
  SET_SUGGESTIONS(state, suggestions) {
    state.suggestions = suggestions;
  },
  SET_LOADING_SESSION(state, val) {
    state.isLoadingSession = val;
  },
  // ── Streaming mutations ──────────────────────────────────────
  STREAM_APPEND(state, { id, text }) {
    const msg = state.currentMessages.find(m => m.id === id);
    // Si el mensaje ya está en estado de error, no acumular más texto
    if (msg && !msg.errored) msg.content += text;
  },
  STREAM_RESET_TEXT(state, { id }) {
    // Limpia la narrativa intermedia entre tool-use loops — solo queda la respuesta final
    const msg = state.currentMessages.find(m => m.id === id);
    if (msg) msg.content = "";
  },
  STREAM_SET_TOOL_STATUS(state, { id, status }) {
    const msg = state.currentMessages.find(m => m.id === id);
    if (msg) msg.toolStatus = status;
  },
  STREAM_ADD_ACTION(state, { id, action }) {
    const msg = state.currentMessages.find(m => m.id === id);
    if (msg) {
      if (!msg.actions) msg.actions = [];
      msg.actions.push(action);
    }
  },
  STREAM_FINALIZE(state, { id, actions }) {
    const msg = state.currentMessages.find(m => m.id === id);
    if (msg) {
      msg.streaming = false;
      delete msg.toolStatus;
      if (actions && actions.length) msg.actions = actions;
    }
  },
  STREAM_SET_ERROR(state, { id, text }) {
    const msg = state.currentMessages.find(m => m.id === id);
    if (msg) {
      msg.streaming = false;
      msg.errored = true; // marca para ignorar deltas posteriores
      delete msg.toolStatus;
      // Sobreescribir SIEMPRE con el mensaje de error — evita concatenar
      // tool output crudo (ej. "Se encontraron 1 clientes") al error.
      msg.content = text;
    }
  },
  // ── Action proposal mutations ────────────────────────────────────
  ADD_ACTION_PROPOSAL(state, proposal) {
    state.currentMessages.push({
      id:
        "proposal_" +
        Date.now() +
        "_" +
        Math.random()
          .toString(36)
          .slice(2),
      role: "system",
      content: "__ACTION_PROPOSAL__",
      proposalData: proposal,
      proposalStatus: "pending", // pending | loading | done | rejected
      proposalResult: null,
      created_at: new Date().toISOString()
    });
  },
  UPDATE_PROPOSAL_STATUS(state, { id, status, result }) {
    const msg = state.currentMessages.find(m => m.id === id);
    if (msg) {
      msg.proposalStatus = status;
      if (result !== undefined) msg.proposalResult = result;
    }
  }
};

const actions = {
  open({ commit, state, dispatch }, payload = {}) {
    commit("SET_OPEN", true);
    if (state.sessions.length === 0) {
      dispatch("fetchSessions");
    }
    dispatch("fetchUsage");
    dispatch("fetchSuggestions");
    if (payload.initialText) {
      // Esperar a que el modal esté montado antes de enviar
      setTimeout(() => {
        // Agregar mensaje optimista del usuario
        commit("ADD_MESSAGE", {
          id: Date.now(),
          role: "user",
          content: payload.initialText,
          created_at: new Date().toISOString()
        });
        dispatch("sendMessage", payload.initialText);
      }, 200);
    }
  },

  close({ commit }) {
    commit("SET_OPEN", false);
  },

  async fetchSuggestions({ commit }) {
    try {
      const { data } = await Api().get("/copilot/suggestions");
      if (data.success && Array.isArray(data.data) && data.data.length) {
        commit("SET_SUGGESTIONS", data.data);
      }
    } catch {
      // Silencioso — el frontend cae a los chips estáticos por defecto.
    }
  },

  async fetchSessions({ commit, state }) {
    try {
      const { data } = await Api().get("/copilot/sessions");
      if (data.success) {
        // Preservar títulos locales que ya fueron actualizados
        const localTitles = {};
        state.sessions.forEach(s => {
          if (s.title && s.title !== "Nueva conversación") {
            localTitles[s.id] = s.title;
          }
        });
        const merged = data.data.map(s => {
          if (
            localTitles[s.id] &&
            (!s.title || s.title === "Nueva conversación")
          ) {
            return { ...s, title: localTitles[s.id] };
          }
          return s;
        });
        commit("SET_SESSIONS", merged);
      }
    } catch (e) {
      // silent
    }
  },

  async newSession({ commit }) {
    try {
      const { data } = await Api().post("/copilot/sessions");
      if (data.success) {
        commit("ADD_SESSION", data.data);
        commit("SET_CURRENT_SESSION", data.data.id);
        commit("SET_CURRENT_MESSAGES", []);
        return data.data;
      }
    } catch (e) {
      // silent
    }
  },

  async loadSession({ commit }, id) {
    commit("SET_LOADING_SESSION", true);
    try {
      const { data } = await Api().get(`/copilot/sessions/${id}`);
      if (data.success) {
        commit("SET_CURRENT_SESSION", data.data.id);
        // Reconstruir mensajes especiales (system) desde el campo meta
        const messages = (data.data.messages || []).map(msg => {
          if (
            msg.role === "system" &&
            msg.content === "__ACTION_PROPOSAL__" &&
            msg.meta
          ) {
            return {
              id: msg.id,
              role: "system",
              content: "__ACTION_PROPOSAL__",
              proposalData: msg.meta,
              proposalStatus: "historical", // histórico: no interactivo
              proposalResult: null,
              created_at: msg.created_at
            };
          }
          return msg;
        });
        commit("SET_CURRENT_MESSAGES", messages);
      }
    } catch (e) {
      // silent
    } finally {
      commit("SET_LOADING_SESSION", false);
    }
  },

  async sendMessage({ commit, state, dispatch }, text) {
    let sessionId = state.currentSessionId;

    if (!sessionId) {
      const pendingMessages = [...state.currentMessages];
      const session = await dispatch("newSession");
      if (!session) return;
      sessionId = session.id;
      pendingMessages.forEach(msg => commit("ADD_MESSAGE", msg));
    }

    commit("SET_SENDING", true);
    commit("SET_LOADING", true);

    // Título optimista
    if (state.currentMessages.filter(m => m.role === "user").length <= 1) {
      const optimisticTitle =
        text.length > 50 ? text.substring(0, 50) + "…" : text;
      commit("UPDATE_SESSION_TITLE", { id: sessionId, title: optimisticTitle });
    }

    // Placeholder del mensaje en streaming — prefijo "ai_" para evitar
    // colisión de ID con el mensaje del usuario (ambos usarían Date.now())
    const streamId = "ai_" + Date.now();
    commit("ADD_MESSAGE", {
      id: streamId,
      role: "assistant",
      content: "",
      streaming: true,
      actions: [],
      created_at: new Date().toISOString()
    });

    const payload = { message: text };
    if (state.pendingConfirmation && state.pendingConfirmation.text === text) {
      payload.confirmed_credits = state.pendingConfirmation.credits;
      commit("CLEAR_PENDING_CONFIRMATION");
    }

    try {
      const currentUser = JSON.parse(localStorage.getItem("user")) || {};
      const apiBase = (process.env.VUE_APP_API_URL || "").replace(/\/$/, "");

      const response = await fetch(
        `${apiBase}/copilot/sessions/${sessionId}/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentUser.token}`,
            Accept: "text/event-stream, application/json"
          },
          body: JSON.stringify(payload)
        }
      );

      // Confirmación de créditos o error HTTP → JSON normal
      if (
        !response.ok ||
        response.headers.get("Content-Type")?.includes("application/json")
      ) {
        const data = await response.json().catch(() => ({}));
        commit("REMOVE_MESSAGE", streamId);

        if (data.data?.needs_confirmation) {
          commit("ADD_MESSAGE", {
            id: Date.now(),
            role: "system",
            content: "__CREDIT_CONFIRMATION__",
            creditData: {
              estimated_credits: data.data.estimated_credits,
              category: data.data.category,
              reason: data.data.reason,
              current_credits: data.data.current_credits,
              original_message: text
            },
            created_at: new Date().toISOString()
          });
          return;
        }

        if (response.status === 401) {
          commit("ADD_MESSAGE", {
            id: Date.now(),
            role: "assistant",
            content:
              "Tu sesión ha expirado. Recarga la página e inicia sesión nuevamente.",
            created_at: new Date().toISOString()
          });
          return;
        }

        commit("ADD_MESSAGE", {
          id: Date.now(),
          role: "assistant",
          content: data.message || "Ocurrió un error. Intenta de nuevo.",
          created_at: new Date().toISOString()
        });
        return;
      }

      // ── Leer stream SSE ──────────────────────────────────────
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let event;
          try {
            event = JSON.parse(line.slice(6));
          } catch {
            continue;
          }

          switch (event.type) {
            case "stream_started":
              // Señal inmediata del backend — feedback visual al usuario
              commit("STREAM_SET_TOOL_STATUS", {
                id: streamId,
                status: "thinking"
              });
              break;

            case "stream_reset":
              // Limpia narración intermedia — solo se mostrará la respuesta final
              commit("STREAM_RESET_TEXT", { id: streamId });
              break;

            case "delta":
              // Primer delta después de tool → limpiar indicador de "pensando"
              commit("STREAM_SET_TOOL_STATUS", { id: streamId, status: null });
              commit("STREAM_APPEND", { id: streamId, text: event.text });
              break;

            case "tool_start":
              commit("STREAM_SET_TOOL_STATUS", {
                id: streamId,
                status: "executing"
              });
              break;

            case "tool_done":
              commit("STREAM_ADD_ACTION", {
                id: streamId,
                action: { tool: event.tool, result: event.result }
              });
              // Mantener spinner hasta que llegue stream_thinking o el siguiente delta
              commit("STREAM_SET_TOOL_STATUS", {
                id: streamId,
                status: "done"
              });
              break;

            case "stream_thinking":
              // Entre tool_use loop y siguiente respuesta de Claude
              commit("STREAM_SET_TOOL_STATUS", {
                id: streamId,
                status: "thinking"
              });
              break;

            case "done":
              commit("STREAM_FINALIZE", {
                id: streamId,
                actions: event.actions
              });
              if (event.credits) commit("SET_CREDITS", event.credits);
              if (
                event.session_title &&
                event.session_title !== "Nueva conversación"
              ) {
                commit("UPDATE_SESSION_TITLE", {
                  id: sessionId,
                  title: event.session_title
                });
              }
              commit("UPDATE_SESSION_PREVIEW", {
                id: sessionId,
                preview: (
                  state.currentMessages.find(m => m.id === streamId)?.content ||
                  ""
                ).substring(0, 60),
                updated_at: new Date().toISOString()
              });
              break;

            case "limit_exceeded":
              commit("REMOVE_MESSAGE", streamId);
              commit("ADD_MESSAGE", {
                id: Date.now(),
                role: "system",
                content: "__LIMIT_EXCEEDED__",
                limitData: event,
                created_at: new Date().toISOString()
              });
              require("@/store").default.dispatch("triggerLimitModal", {
                feature: "ai_credits_monthly",
                usage: {
                  used: event.current ?? 0,
                  limit: event.max ?? 0,
                  percentage: event.max
                    ? Math.round((event.current / event.max) * 100)
                    : 100
                }
              });
              break;

            case "action_proposal":
              // Write tool requiere aprobación — insertar tarjeta antes del mensaje IA
              commit("ADD_ACTION_PROPOSAL", {
                action: event.action,
                params: event.params,
                preview: event.preview
              });
              break;

            case "ping":
              // Heartbeat — ignorar en el frontend
              break;

            case "error":
              commit("STREAM_SET_ERROR", {
                id: streamId,
                text: event.message || "Ocurrió un error. Intenta de nuevo."
              });
              break;
          }
        }
      }
    } catch (e) {
      // Error de red o fetch abortado
      commit("STREAM_SET_ERROR", {
        id: streamId,
        text: "Error de conexión. Verifica tu internet e intenta de nuevo."
      });
    } finally {
      commit("SET_LOADING", false);
      commit("SET_SENDING", false);
    }
  },

  confirmCredits({ commit, dispatch }, { text, credits }) {
    commit("SET_PENDING_CONFIRMATION", { text, credits });
    dispatch("sendMessage", text);
  },

  async fetchUsage({ commit }) {
    try {
      const { data } = await Api().get("/copilot/usage");
      if (data.success) {
        commit("SET_USAGE", data.data);
        if (data.data.credits) {
          commit("SET_CREDITS", data.data.credits);
        }
      }
    } catch (e) {
      // silent
    }
  },

  async deleteSession({ commit }, id) {
    try {
      await Api().delete(`/copilot/sessions/${id}`);
      commit("REMOVE_SESSION", id);
    } catch (e) {
      // silent
    }
  },

  /**
   * Ejecutar una acción aprobada por el usuario.
   * Llama al backend con los parámetros que Claude preparó.
   */
  async executeAction({ commit, state }, { proposalId, action, params }) {
    commit("UPDATE_PROPOSAL_STATUS", { id: proposalId, status: "loading" });

    // Reintentar hasta 2 veces ante errores transitorios (red, timeout 5xx)
    const attempt = async () => {
      const { data } = await Api().post("/copilot/execute-action", {
        action,
        params,
        session_id: state.currentSessionId
      });
      return data;
    };

    let lastError = null;
    for (let i = 0; i < 3; i++) {
      try {
        const data = await attempt();
        // Si el backend respondió con success false pero dato útil, no reintentar
        if (data && (data.success || data.data?.success)) {
          commit("UPDATE_PROPOSAL_STATUS", {
            id: proposalId,
            status: "done",
            result: data.data || data
          });
          return { success: true, result: data.data || data };
        }
        // Backend dijo success=false → no reintentar (error de validación, no transitorio)
        const errMsg =
          data?.data?.error ||
          data?.message ||
          "No se pudo ejecutar la acción.";
        commit("UPDATE_PROPOSAL_STATUS", {
          id: proposalId,
          status: "error",
          result: { error: errMsg }
        });
        return { success: false, result: { error: errMsg } };
      } catch (e) {
        lastError = e;
        const status = e?.response?.status;
        // Solo reintentar errores transitorios (red, 5xx)
        if (status && status < 500 && status !== 408 && status !== 429) {
          break;
        }
        // Esperar 800ms, 1600ms entre reintentos
        if (i < 2) await new Promise(r => setTimeout(r, 800 * (i + 1)));
      }
    }

    const errMsg =
      lastError?.response?.data?.data?.error ||
      lastError?.response?.data?.message ||
      "No se pudo ejecutar la acción. Intenta de nuevo.";
    commit("UPDATE_PROPOSAL_STATUS", {
      id: proposalId,
      status: "error",
      result: { error: errMsg }
    });
    return { success: false, result: { error: errMsg } };
  },

  rejectAction({ commit }, { proposalId }) {
    commit("UPDATE_PROPOSAL_STATUS", { id: proposalId, status: "rejected" });
  }
};

const getters = {
  isOpen: state => state.isOpen,
  sessions: state => state.sessions,
  currentSessionId: state => state.currentSessionId,
  currentMessages: state => state.currentMessages,
  isLoading: state => state.isLoading,
  isSending: state => state.isSending,
  usage: state => state.usage,
  credits: state => state.credits,
  isLoadingSession: state => state.isLoadingSession,
  suggestions: state => state.suggestions
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
